import { applyEtaToBoutiques } from './clickCollectEta';

const STATUS_AVAILABLE = 1;
const STATUS_LOW = 2;

function extractLocations(rawResponse) {
  if (Array.isArray(rawResponse)) return rawResponse;
  if (Array.isArray(rawResponse?.locations)) return rawResponse.locations;
  if (Array.isArray(rawResponse?.data)) return rawResponse.data;
  return [];
}

function extractPickups(location) {
  const raw = location.pickups || location.items || location.stock;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;

  if (typeof raw === 'object') {
    return Object.entries(raw).map(([sku, data]) => ({
      sku,
      status: Number(data?.status ?? 0),
      quantity: Number(data?.quantity ?? 0),
    }));
  }

  return [];
}

function mergeWarehouseStock(pickups, location, warehouseTransferEnabled) {
  if (!warehouseTransferEnabled) return pickups;

  const warehousePickups = location.warehousePickups || location.warehouse_stock || [];
  if (!Array.isArray(warehousePickups) || !warehousePickups.length) return pickups;

  const merged = new Map(pickups.map((p) => [p.sku, { ...p }]));

  warehousePickups.forEach((warehouseItem) => {
    const existing = merged.get(warehouseItem.sku);
    if (!existing) {
      merged.set(warehouseItem.sku, {
        ...warehouseItem,
        warehouseAvailable: warehouseItem.status === STATUS_AVAILABLE,
      });
      return;
    }

    if (warehouseItem.status === STATUS_AVAILABLE) {
      merged.set(warehouseItem.sku, {
        ...existing,
        status: STATUS_AVAILABLE,
        quantity: (existing.quantity || 0) + (warehouseItem.quantity || 0),
        warehouseAvailable: true,
      });
    }
  });

  return Array.from(merged.values());
}

function mapSkuStatuses(pickups) {
  return pickups.map((item) => ({
    sku: item.sku,
    status: Number(item.status),
    quantity: Number(item.quantity || 0),
    warehouseAvailable: Boolean(item.warehouseAvailable),
  }));
}

export function computeAggregateStatus(skuStatuses, skus = []) {
  if (!skus.length) {
    if (!skuStatuses.length) return 'unavailable';
    if (skuStatuses.every((s) => s.status === STATUS_AVAILABLE)) return 'available';
    if (skuStatuses.some((s) => s.status === STATUS_LOW && s.quantity > 0)) return 'low';
    return 'unavailable';
  }

  const statusMap = new Map(skuStatuses.map((s) => [s.sku, s]));
  let hasLow = false;

  for (const sku of skus) {
    const status = statusMap.get(sku);
    if (!status || status.status !== STATUS_AVAILABLE) {
      if (status?.status === STATUS_LOW && status.quantity > 0) {
        hasLow = true;
        continue;
      }
      return 'unavailable';
    }
  }

  return hasLow ? 'low' : 'available';
}

export function getStatusLabel(status) {
  switch (status) {
    case 'available':
      return 'Available';
    case 'low':
      return 'Low Stock';
    default:
      return 'Unavailable';
  }
}

export function transformHyperspaceStoreData(rawResponse, options = {}) {
  const {
    allowedBoutiqueIds = [],
    skus = [],
    warehouseTransferEnabled = false,
    config = window.theme?.clickAndCollect,
  } = options;

  const locations = extractLocations(rawResponse);

  const boutiques = locations
    .filter((loc) => {
      if (!allowedBoutiqueIds.length) return true;
      return allowedBoutiqueIds.includes(Number(loc.id));
    })
    .map((loc) => {
      const pickups = mergeWarehouseStock(
        extractPickups(loc),
        loc,
        warehouseTransferEnabled
      );
      const skuStatuses = mapSkuStatuses(pickups);
      const aggregateStatus = computeAggregateStatus(skuStatuses, skus);

      return {
        id: String(loc.id),
        name: loc.name || loc.storename || '',
        distance: loc.distance,
        lat: loc.lat ?? loc.coordinates?.latitude ?? null,
        lng: loc.lng ?? loc.coordinates?.longitude ?? null,
        address: loc.address,
        phone: loc.phone || loc.phone_number || '',
        email: loc.email || '',
        openHours: loc.openHours || loc.open_hours || loc.opening_hours || loc.openhours || '',
        url: loc.url || loc.map_link || loc.website_url || '',
        warehouseNodeId: loc.warehouseNodeId || loc.warehouse_node_id || null,
        skuStatuses,
        aggregateStatus,
        statusLabel: getStatusLabel(aggregateStatus),
      };
    });

  return applyEtaToBoutiques(boutiques, skus, config);
}

export function filterInStockOnly(boutiques) {
  return boutiques.filter((b) => b.aggregateStatus === 'available');
}

export function sortPreferredFirst(boutiques, preferredBoutique) {
  if (!preferredBoutique?.id) return boutiques;

  const preferredId = String(preferredBoutique.id);
  return [...boutiques].sort((a, b) => {
    if (String(a.id) === preferredId) return -1;
    if (String(b.id) === preferredId) return 1;
    return 0;
  });
}

export function getUnavailableSkus(boutique, skus = []) {
  if (!boutique) return skus;
  const statusMap = new Map((boutique.skuStatuses || []).map((s) => [s.sku, s]));
  return skus.filter((sku) => {
    const status = statusMap.get(sku);
    return !status || status.status !== STATUS_AVAILABLE;
  });
}
